import { createFileRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { roleApi } from "@/lib/api/services/role.api";
import { useLingui } from "@lingui/react/macro";
import {
  ShieldCheck,
  Users,
  Loader2,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PermissionMatrix,
  RoleDialog,
  RoleSheet,
  ScopeAssignmentsTable,
  UserAssignmentsTab,
} from "@/components/permissions";
import { RestoreDefaultRolesButton } from "@/components/support/RestoreDefaultRolesCard";
import { RolesTable } from "@/components/permissions";
import type { Role } from "@/lib/api/admin-api";
import { CONCEPT_ADMIN_ROLE_CODES } from "@/lib/access-control/concept-admin";

const DEFAULT_ROLE_CODES = new Set([
  'SUPER_ADMIN', 'SUPPORT', 'ORG_ADMIN',
  ...CONCEPT_ADMIN_ROLE_CODES,
  'REGION_MANAGER', 'LOCATION_MANAGER', 'CHEF',
]);

function isDefaultRole(role: Role) {
  return role.isSystem || DEFAULT_ROLE_CODES.has(role.code);
}

export const Route = createFileRoute("/(protected)/_layout/rolesperms")({
  validateSearch: (search) =>
    z.object({
      roleId: z.coerce.number().optional(),
      tab: z.enum(["roles", "users"]).optional().default("roles"),
    }).parse(search),
  component: RolesPermsPage,
});

function RolesPermsPage() {
  const isTablet = useIsTablet();
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const { isAuthenticated, isLoading: authLoading, hasRole } = useAuth();
  const { roleId, tab } = Route.useSearch();
  const [matrixExpanded, setMatrixExpanded] = useState(true);
  const [scopesExpanded, setScopesExpanded] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const isSuperAdmin = hasRole("SUPER_ADMIN");

  const { data: roles = [], isLoading: rolesLoading, isFetching } = useQuery({
    queryKey: ["roles"],
    queryFn: () => roleApi.getAll(),
    enabled: isAuthenticated && isSuperAdmin,
  });

  const { data: permissionGroups = [], isLoading: permsLoading } = useQuery({
    queryKey: ["roles", "permissions"],
    queryFn: () => roleApi.getPermissions(),
    enabled: isAuthenticated && isSuperAdmin,
  });

  const { data: scopeAssignments = [] } = useQuery({
    queryKey: ["roles", "scopes"],
    queryFn: () => roleApi.getScopes(),
    enabled: isAuthenticated && isSuperAdmin,
  });

  const isLoading = authLoading || rolesLoading || permsLoading;

  if (authLoading) {
    return <PermissionsSkeleton />;
  }

  if (!isSuperAdmin) {
    return (
      <Navigate
        to="/unauthorized"
        search={{ reason: "super_admin_only", from: "/rolesperms", requiredRole: "SUPER_ADMIN" }}
        replace
      />
    );
  }

  if (isLoading && roles.length === 0) {
    return <PermissionsSkeleton />;
  }

  const systemRoles = roles.filter((r) => r.isSystem);
  const customRoles = roles.filter((r) => !r.isSystem);

  return (
    <div className="flex flex-col gap-5 py-5 px-3">
      {/* Page header */}
      {isTablet ? (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
              {t`Role/Permissions`}
            </h1>
            {isFetching && (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-(--color-text-secondary)" />
            )}
            {tab === "roles" && (
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 gap-2"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ["roles"] })}
                >
                  <RefreshCcw className={isFetching ? "animate-spin" : ""} />
                  {t`Refresh`}
                </Button>
                <RestoreDefaultRolesButton />
                <RoleDialog permissionGroups={permissionGroups} />
              </div>
            )}
          </div>
        </PageHeader>
      ) : (
        <div className="flex items-center justify-between px-3">
          <div>
            <div className="flex gap-3 items-center">
              <h1 className="text-5xl font-bold tracking-tight text-(--color-text-primary)">
                {t`Role/Permissions`}
                {isFetching && (
                  <Loader2 className="inline ml-3 h-6 w-6 animate-spin text-(--color-text-secondary)" />
                )}
              </h1>
            </div>
            <p className="text-(--color-text-secondary) mt-1">
              {t`Manage roles, permissions, scope assignments, and user role assignment.`}
            </p>
          </div>
          {tab === "roles" && (
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["roles"] })}
              >
                <RefreshCcw className={isFetching ? "animate-spin" : ""} />
                {t`Refresh`}
              </Button>
              <RestoreDefaultRolesButton />
              <RoleDialog permissionGroups={permissionGroups} />
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs
        value={tab}
        onValueChange={(v) =>
          void navigate({ search: (prev) => ({ ...prev, tab: v as "roles" | "users" }) })
        }
      >
        <TabsList className="mx-3">
          <TabsTrigger value="roles" className="gap-2">
            <ShieldCheck className="h-4 w-4" />
            {t`Roles & Permissions`}
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            {t`User Assignments`}
          </TabsTrigger>
        </TabsList>

        {/* ── Roles & Permissions tab ── */}
        <TabsContent value="roles" className="flex flex-col gap-5 mt-5">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 p-3 bg-(--color-surface-muted) rounded-lg">
            <StatCard
              label={t`Total Roles`}
              value={roles.length}
              icon={ShieldCheck}
            />
            <StatCard
              label={t`System Roles`}
              value={systemRoles.length}
              icon={ShieldCheck}
            />
            <StatCard
              label={t`Custom Roles`}
              value={customRoles.length}
              icon={ShieldCheck}
            />
          </div>

          {/* Roles table */}
          <RolesTable
            roles={roles}
            onSelect={setSelectedRole}
            isProtected={isDefaultRole}
            emptyMessage={t`No roles found`}
            className="rounded-lg border overflow-x-auto [&_table]:min-w-160"
          />

          {/* Permission matrix */}
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => setMatrixExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-(--color-surface-muted) border-b text-sm font-medium hover:bg-(--color-surface-muted) transition-colors"
            >
              <span>{t`Permission Matrix`}</span>
              {matrixExpanded ? (
                <ChevronUp className="h-4 w-4 text-(--color-text-secondary)" />
              ) : (
                <ChevronDown className="h-4 w-4 text-(--color-text-secondary)" />
              )}
            </button>
            {matrixExpanded && (
              <PermissionMatrix key={roleId} roles={roles} permissionGroups={permissionGroups} defaultRoleId={roleId} />
            )}
          </div>

          {/* Scope assignments */}
          <div className="rounded-lg border">
            <button
              type="button"
              onClick={() => setScopesExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-(--color-surface-muted) border-b text-sm font-medium hover:bg-(--color-surface-muted) transition-colors"
            >
              <span>{t`Scope Assignments`}</span>
              {scopesExpanded ? (
                <ChevronUp className="h-4 w-4 text-(--color-text-secondary)" />
              ) : (
                <ChevronDown className="h-4 w-4 text-(--color-text-secondary)" />
              )}
            </button>
            {scopesExpanded && (
              <ScopeAssignmentsTable roles={roles} assignments={scopeAssignments} />
            )}
          </div>
        </TabsContent>

        {/* ── User Assignments tab ── */}
        <TabsContent value="users" className="mt-5 px-1">
          <UserAssignmentsTab />
        </TabsContent>
      </Tabs>

      <RoleSheet
        role={selectedRole}
        onClose={() => setSelectedRole(null)}
        permissionGroups={permissionGroups}
        scopeAssignments={scopeAssignments}
        onRoleDeleted={() => {
          setSelectedRole(null);
          queryClient.invalidateQueries({ queryKey: ["roles"] });
        }}
      />
    </div>
  );
}

function PermissionsSkeleton() {
  return (
    <div className="space-y-6 p-5">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-12 w-48" />
          <Skeleton className="mt-2 h-4 w-60" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <div className="rounded-lg border">
        <div className="p-4 space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}
